import { useEffect, useRef, useState } from "react";
import { Loader2, Upload, X, Search, Check } from "lucide-react";
import { toast } from "sonner";
import { api, API, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";

const grosz2zl = (g) => (g ? (g / 100).toString().replace(".", ",") : "");
const zl2grosz = (s) => Math.round(parseFloat(String(s).replace(",", ".") || "0") * 100) || 0;
const toArr = (s) => String(s || "").split(",").map((x) => x.trim()).filter(Boolean);

export function ShopProductForm({ product, onSaved, onCancel }) {
  const isEdit = !!product?.id;
  const [f, setF] = useState(() => ({
    name: product?.name || "", brand: product?.brand || "", subtitle: product?.subtitle || "", description: product?.description || "",
    stock: product?.stock ?? 0,
    category: product?.category || "", composition: product?.composition || "", gramatura: product?.gramatura || "",
    kroj: product?.kroj || "", dekolt: product?.dekolt || "", szwy: product?.szwy || "", base_sku: product?.base_sku || "",
    price: grosz2zl(product?.price_grosz), compare: grosz2zl(product?.compare_at_grosz),
    colors: (product?.colors || []).join(", "), sizes: (product?.sizes || []).join(", "),
    active: product?.active ?? false,
    bestseller: product?.bestseller ?? false,
  }));
  const [photos, setPhotos] = useState(product?.photos || []);
  const [colorPhotos, setColorPhotos] = useState(product?.color_photos || {});
  const [gpsr, setGpsr] = useState(product?.gpsr || {});
  const [base, setBase] = useState([]);
  const [basePick, setBasePick] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => { api.get("/shop/admin/base-products").then((r) => setBase(r.data || [])).catch(() => {}); }, []);

  const applyBase = (b) => {
    setF((p) => ({ ...p, base_sku: b.sku, category: b.type || p.category, composition: b.sklad || p.composition,
      gramatura: b.gramatura || p.gramatura, kroj: b.kroj || p.kroj, dekolt: b.dekolt || p.dekolt, szwy: b.szwy || p.szwy,
      colors: (b.colors || []).join(", ") || p.colors, sizes: (b.sizes || []).join(", ") || p.sizes }));
    setBasePick(false);
    toast.success("Pobrano dane z bazy produkcyjnej — pola pozostają w pełni edytowalne");
  };

  const upload = async (e) => {
    const files = Array.from(e.target.files || []); e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post("/files", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos((p) => [...p, data.id]);
      }
    } catch (err) { toast.error(errMsg(err)); } finally { setUploading(false); }
  };

  const save = async () => {
    if (!f.name.trim()) return toast.error("Podaj nazwę produktu (widoczną dla klienta)");
    if (zl2grosz(f.price) <= 0) return toast.error("Podaj cenę brutto");
    setBusy(true);
    const payload = {
      name: f.name, brand: f.brand, subtitle: f.subtitle, description: f.description, category: f.category,
      stock: parseInt(f.stock, 10) || 0,
      composition: f.composition, gramatura: f.gramatura, kroj: f.kroj, dekolt: f.dekolt, szwy: f.szwy,
      base_sku: f.base_sku, price_grosz: zl2grosz(f.price), compare_at_grosz: zl2grosz(f.compare),
      photos, color_photos: Object.fromEntries(Object.entries(colorPhotos).filter(([, v]) => v && photos.includes(v))),
      colors: toArr(f.colors), sizes: toArr(f.sizes), active: f.active, bestseller: f.bestseller, gpsr,
    };
    try {
      if (isEdit) await api.put(`/shop/admin/products/${product.id}`, payload);
      else await api.post("/shop/admin/products", payload);
      toast.success(isEdit ? "Zapisano zmiany" : "Dodano produkt");
      onSaved();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="shop-product-form">
      <div className="flex flex-wrap items-center gap-3">
        <Popover open={basePick} onOpenChange={setBasePick}>
          <PopoverTrigger asChild>
            <Button data-testid="base-product-picker" variant="outline" className="gap-2 border-dashed"><Search className="h-4 w-4" />Pobierz dane z bazy produkcyjnej{f.base_sku ? ` (${f.base_sku})` : ""}</Button>
          </PopoverTrigger>
          <PopoverContent className="p-0 w-[360px]" align="start">
            <Command>
              <CommandInput placeholder="Szukaj w bazie (nazwa, SKU)…" />
              <CommandList>
                <CommandEmpty>Brak wyników</CommandEmpty>
                <CommandGroup>
                  {base.map((b) => (
                    <CommandItem key={b.sku} value={`${b.name} ${b.sku} ${b.type}`} onSelect={() => applyBase(b)}>
                      <div className="min-w-0"><p className="truncate text-sm">{b.name}</p><p className="truncate text-xs text-muted-foreground">{b.type} · skład: {b.sklad || "—"}</p></div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <span className="text-xs text-muted-foreground">Baza służy do pobrania składu/specyfikacji. Nazwy i numery katalogowe nie są pokazywane klientom.</span>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2"><Label className="text-xs">Nazwa produktu (dla klienta) *</Label><Input data-testid="pf-name" value={f.name} onChange={(e) => set("name", e.target.value)} className="mt-1" placeholder="np. Koszulka Premium z nadrukiem" /></div>
        <div className="sm:col-span-2"><Label className="text-xs">Podtytuł / krótki opis</Label><Input data-testid="pf-subtitle" value={f.subtitle} onChange={(e) => set("subtitle", e.target.value)} className="mt-1" /></div>
        <div className="sm:col-span-2"><Label className="text-xs">Opis</Label><Textarea data-testid="pf-desc" value={f.description} onChange={(e) => set("description", e.target.value)} rows={3} className="mt-1" /></div>
        <div><Label className="text-xs">Kategoria</Label><Input data-testid="pf-cat" value={f.category} onChange={(e) => set("category", e.target.value)} className="mt-1" placeholder="np. Koszulki" /></div>
        <div><Label className="text-xs">Marka (profil GPSR uzupełni się automatycznie)</Label><Input data-testid="pf-brand" value={f.brand} onChange={(e) => set("brand", e.target.value)} className="mt-1" placeholder="np. VESTI" /></div>
        <div><Label className="text-xs">Skład</Label><Input value={f.composition} onChange={(e) => set("composition", e.target.value)} className="mt-1" /></div>
        <div><Label className="text-xs">Gramatura</Label><Input value={f.gramatura} onChange={(e) => set("gramatura", e.target.value)} className="mt-1" /></div>
        <div><Label className="text-xs">Krój</Label><Input value={f.kroj} onChange={(e) => set("kroj", e.target.value)} className="mt-1" /></div>
        <div className="grid grid-cols-2 gap-3 sm:col-span-2">
          <div><Label className="text-xs">Cena brutto (zł) *</Label><Input data-testid="pf-price" value={f.price} onChange={(e) => set("price", e.target.value)} className="mt-1" placeholder="59,99" />
            <p className="mt-1 text-[11px] text-muted-foreground" data-testid="pf-price-net">{zl2grosz(f.price) > 0 ? `Netto: ${grosz2zl(Math.round(zl2grosz(f.price) / 1.23))} zł · VAT 23%: ${grosz2zl(zl2grosz(f.price) - Math.round(zl2grosz(f.price) / 1.23))} zł` : "Cena z VAT 23% — netto wyliczy się automatycznie"}</p>
          </div>
          <div><Label className="text-xs">Stan magazynowy (szt.)</Label><Input data-testid="pf-stock" type="number" min="0" value={f.stock} onChange={(e) => set("stock", e.target.value)} className="mt-1" placeholder="0" />
            <p className="mt-1 text-[11px] text-muted-foreground">{Number(f.stock) > 0 ? "Na karcie: „Dostępne”" : "Na karcie: „Czas realizacji: około 7 dni”"}</p>
          </div>
          <div><Label className="text-xs">Stara cena / promocja (zł)</Label><Input data-testid="pf-compare" value={f.compare} onChange={(e) => set("compare", e.target.value)} className="mt-1" placeholder="opcjonalnie" /></div>
        </div>
        <div><Label className="text-xs">Dostępne kolory (po przecinku)</Label><Input data-testid="pf-colors" value={f.colors} onChange={(e) => set("colors", e.target.value)} className="mt-1" placeholder="BLACK, WHITE, NAVY" /></div>
        <div><Label className="text-xs">Dostępne rozmiary (po przecinku)</Label><Input data-testid="pf-sizes" value={f.sizes} onChange={(e) => set("sizes", e.target.value)} className="mt-1" placeholder="S, M, L, XL" /></div>
      </div>

      <div>
        <Label className="text-xs">Zdjęcia produktu</Label>
        <div className="mt-2 flex flex-wrap gap-3">
          {photos.map((ph, i) => (
            <div key={ph} className="relative h-24 w-24 rounded-lg overflow-hidden border border-border bg-white">
              <img src={`${API}/files/${ph}`} alt="" className="h-full w-full object-contain bg-white p-1" />
              <button onClick={() => setPhotos((p) => p.filter((_, idx) => idx !== i))} className="absolute top-1 right-1 rounded-full bg-black/70 p-1 text-white"><X className="h-3 w-3" /></button>
            </div>
          ))}
          <button data-testid="pf-upload" onClick={() => fileRef.current?.click()} disabled={uploading} className="grid h-24 w-24 place-items-center rounded-lg border-2 border-dashed border-border text-muted-foreground hover:border-amber-400">
            {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
          </button>
          <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={upload} />
        </div>
      </div>

      {toArr(f.colors).length > 0 && photos.length > 0 && (
        <div data-testid="pf-color-photos">
          <Label className="text-xs">Zdjęcie dla koloru (opcjonalnie — klient zobaczy je po wybraniu koloru)</Label>
          <div className="mt-2 space-y-2">
            {toArr(f.colors).map((c) => (
              <div key={c} className="flex items-center gap-2">
                <span className="w-28 shrink-0 text-xs font-medium uppercase text-muted-foreground">{c}</span>
                <div className="flex flex-wrap gap-1.5">
                  {photos.map((ph) => (
                    <button key={ph} type="button" title={colorPhotos[c] === ph ? "Przypisane" : "Przypisz do koloru"}
                      onClick={() => setColorPhotos((m) => ({ ...m, [c]: m[c] === ph ? undefined : ph }))}
                      className={`h-10 w-10 overflow-hidden rounded border-2 bg-white transition ${colorPhotos[c] === ph ? "border-amber-400" : "border-transparent opacity-50 hover:opacity-100"}`}>
                      <img src={`${API}/files/${ph}`} alt="" className="h-full w-full object-contain p-0.5" />
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div data-testid="pf-gpsr" className="rounded-lg border border-border p-3">
        <Label className="text-xs font-bold uppercase tracking-wider text-amber-600">Producent i bezpieczeństwo (GPSR)</Label>
        <div className="mt-2 grid sm:grid-cols-2 gap-3">
          <div><Label className="text-xs">Producent</Label><Input value={gpsr.manufacturer || ""} onChange={(e) => setGpsr((g) => ({ ...g, manufacturer: e.target.value }))} className="mt-1" placeholder="np. Innova S.p.A." /></div>
          <div><Label className="text-xs">Marka</Label><Input value={gpsr.brand || ""} onChange={(e) => setGpsr((g) => ({ ...g, brand: e.target.value }))} className="mt-1" /></div>
          <div><Label className="text-xs">Model / identyfikacja</Label><Input value={gpsr.model_id || ""} onChange={(e) => setGpsr((g) => ({ ...g, model_id: e.target.value }))} className="mt-1" /></div>
          <div><Label className="text-xs">Adres</Label><Input value={gpsr.address || ""} onChange={(e) => setGpsr((g) => ({ ...g, address: e.target.value }))} className="mt-1" /></div>
          <div><Label className="text-xs">E-mail</Label><Input value={gpsr.email || ""} onChange={(e) => setGpsr((g) => ({ ...g, email: e.target.value }))} className="mt-1" /></div>
          <div><Label className="text-xs">Telefon</Label><Input value={gpsr.phone || ""} onChange={(e) => setGpsr((g) => ({ ...g, phone: e.target.value }))} className="mt-1" /></div>
          <div className="sm:col-span-2"><Label className="text-xs">Osoba odpowiedzialna w UE (tylko producenci spoza UE)</Label><Input value={gpsr.eu_responsible || ""} onChange={(e) => setGpsr((g) => ({ ...g, eu_responsible: e.target.value }))} className="mt-1" placeholder="nie dotyczy — producent z UE" /></div>
          <div className="sm:col-span-2"><Label className="text-xs">Ostrzeżenia / instrukcje (tylko z dokumentacji)</Label><Textarea value={gpsr.warnings || ""} onChange={(e) => setGpsr((g) => ({ ...g, warnings: e.target.value }))} rows={2} className="mt-1" /></div>
        </div>
      </div>

      <label className="flex items-center gap-2 text-sm"><Checkbox data-testid="pf-active" checked={f.active} onCheckedChange={(v) => set("active", v)} />Opublikowany (widoczny w sklepie)</label>
      <label className="flex items-center gap-2 text-sm"><Checkbox data-testid="pf-bestseller" checked={f.bestseller} onCheckedChange={(v) => set("bestseller", v)} />Bestseller (wyróżniony w sekcji „Bestsellery” na stronie sklepu)</label>

      <div className="flex justify-end gap-3 border-t border-border pt-4">
        <Button variant="outline" onClick={onCancel}>Anuluj</Button>
        <Button data-testid="pf-save" onClick={save} disabled={busy} className="gap-2 bg-amber-400 text-[#0D1117] hover:bg-amber-500 font-bold">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}{isEdit ? "Zapisz zmiany" : "Dodaj produkt"}</Button>
      </div>
    </div>
  );
}
